<template>
  <form class="ai-composer" :class="{ 'is-focused': focused, 'is-disabled': disabled }" @submit.prevent="submit">
    <label class="sr-only" for="kars-ai-input">{{ $gt('Ihre Frage an KARS-KI') }}</label>
    <textarea
      id="kars-ai-input"
      ref="field"
      v-model="text"
      class="ai-composer__input"
      data-testid="ai-input"
      :disabled="disabled"
      :placeholder="placeholder"
      rows="1"
      @blur="focused = false"
      @focus="focused = true"
      @input="autoGrow"
      @keydown.enter.exact.prevent="submit"
    />
    <button
      v-if="streaming"
      :aria-label="$gt('Antwort stoppen')"
      class="ai-composer__send is-stop"
      data-testid="ai-stop"
      type="button"
      @click="$emit('stop')"
    >
      <Square fill="currentColor" :size="13" />
    </button>
    <button
      v-else
      :aria-label="$gt('Senden')"
      class="ai-composer__send"
      :class="{ 'is-ready': canSend }"
      data-testid="ai-send"
      :disabled="!canSend"
      type="submit"
    >
      <ArrowUp :size="18" />
    </button>
  </form>
</template>

<script setup lang="ts">
  import { ArrowUp, Square } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import { gt } from '@/i18n/gt'

  const props = defineProps<{
    streaming: boolean
    disabled: boolean
    /** Vorbelegung von außen (Hilfe-Center, Vorschlags-Chips). */
    prefill?: string | null
  }>()

  const emit = defineEmits<{
    (e: 'send', text: string): void
    (e: 'stop' | 'consumed'): void
  }>()

  const MAX_ROWS = 6
  const text = ref('')
  const focused = ref(false)
  const field = ref<HTMLTextAreaElement | null>(null)

  const canSend = computed(() => text.value.trim().length > 0 && !props.disabled && !props.streaming)
  const placeholder = computed(() => (props.disabled
    ? gt('Der Assistent ist derzeit nicht verfügbar.')
    : gt('z. B. „Wo stelle ich Benachrichtigungen ein?“')))

  function autoGrow () {
    const el = field.value
    if (!el) {
      return
    }
    el.style.height = 'auto'
    const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight) || 20
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * MAX_ROWS + 16)}px`
  }

  function submit () {
    if (!canSend.value) {
      return
    }
    emit('send', text.value)
    text.value = ''
    void nextTick(autoGrow)
  }

  function focus () {
    field.value?.focus()
  }

  watch(() => props.prefill, value => {
    if (!value) {
      return
    }
    text.value = value
    emit('consumed')
    void nextTick(() => {
      autoGrow()
      focus()
    })
  }, { immediate: true })

  defineExpose({ focus })
</script>

<style scoped>
  .ai-composer {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: end;
    padding: 6px 6px 6px 12px;
    border: 1px solid var(--ai-line-strong);
    border-radius: 10px;
    background: var(--ai-surface);
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }

  .ai-composer:hover {
    border-color: #b6bec9;
  }

  .ai-composer.is-focused {
    border-color: var(--ai-blue);
    box-shadow: 0 0 0 3px var(--ai-blue-ring);
  }

  .ai-composer.is-disabled {
    background: var(--ai-surface-2);
  }

  .ai-composer__input {
    width: 100%;
    max-height: 160px;
    padding: 8px 0;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    color: var(--ai-ink);
    font: inherit;
    font-size: 14px;
    line-height: 20px;
  }

  .ai-composer__input::placeholder {
    color: var(--ai-faint);
  }

  .ai-composer__send {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: var(--ai-surface-3);
    color: var(--ai-faint);
    cursor: pointer;
    transition: background-color 150ms ease, color 150ms ease;
  }

  .ai-composer__send.is-ready {
    background: var(--ai-blue);
    color: #fff;
  }

  .ai-composer__send.is-ready:hover {
    background: var(--ai-blue-dark);
  }

  .ai-composer__send:disabled {
    cursor: not-allowed;
  }

  .ai-composer__send.is-stop {
    background: var(--ai-navy);
    color: #fff;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
</style>
